export const COVERAGE_LIST_TYPES = {
	BLACKLIST: "blacklist",
	WHITELIST: "whitelist",
};

export const BLACK_WHITE_LISTS_PATH = "/black_&_white_lists";
export const BLACK_WHITE_LISTS_TITLE = "Black & White Lists";

const decodePathname = (pathname) => {
	try {
		return decodeURIComponent(pathname || "");
	} catch {
		return pathname || "";
	}
};

export const isBlackWhiteListsPath = (pathname) => {
	const decoded = decodePathname(pathname).replace(/\/+$/, "") || "/";
	return decoded === BLACK_WHITE_LISTS_PATH;
};

export const parseBlackWhiteListsUrl = (urlString, base) => {
	if (!urlString) return null;

	try {
		const url = new URL(urlString, base);
		if (!isBlackWhiteListsPath(url.pathname)) return null;

		const table =
			url.searchParams.get("table")?.trim().toLowerCase() || "";
		const id =
			url.searchParams.get("id")?.trim() ||
			url.searchParams.get("record_id")?.trim() ||
			url.searchParams.get("recordId")?.trim() ||
			"";
		const mode =
			url.searchParams.get("mode")?.trim().toLowerCase() || "edit";

		return { table, id, mode };
	} catch {
		return null;
	}
};

export const COVERAGE_META_FIELDS = new Set([
	"id",
	"table_key",
	"edit",
	"json_schema",
]);

export const CUSTOMER_EDITABLE_FIELDS = [
	"id_egon_civico",
	"id_egon_via",
	"civico_top_egon",
	"status",
];

export const BLACKLIST_POP_SKY_SITE_KEY = "SKY SITE CODE";

export const BLACKLIST_POP_SKY_SITE_ALIASES = [
	BLACKLIST_POP_SKY_SITE_KEY,
	"SKY_SITE_CODE",
	"sky_site_code",
];

/** Canonical POP fields for both Add and Edit (keys = API property names). */
export const BLACKLIST_POP_FIELDS = [
	BLACKLIST_POP_SKY_SITE_KEY,
	"status",
];

export const LOCKED_ON_EDIT_FIELDS = new Set([
	"id_egon_civico",
	"id_egon_via",
	"civico_top_egon",
]);

export const ADD_REQUIRED_FIELDS = ["id_egon_civico", "status"];
export const EDIT_REQUIRED_FIELDS = ["status"];

export const BLACKLIST_POP_ADD_REQUIRED_FIELDS = [...BLACKLIST_POP_FIELDS];
export const BLACKLIST_POP_EDIT_REQUIRED_FIELDS = [];

const isFilled = (value) =>
	value != null && String(value).trim() !== "" && String(value).trim() !== "null";

const FIELD_DEFS = {
	id_egon_civico: {
		title: "Egon Civico ID",
		type: "text",
		md: 6,
		tab: "Details",
	},
	id_egon_via: {
		title: "Egon Via ID",
		type: "text",
		md: 6,
		tab: "Details",
	},
	civico_top_egon: {
		title: "Civico Top Egon",
		type: "text",
		md: 6,
		tab: "Details",
	},
	[BLACKLIST_POP_SKY_SITE_KEY]: {
		title: "Sky Site Code",
		type: "text",
		md: 6,
		tab: "Details",
	},
	status: {
		title: "Status",
		type: "enum",
		enum: ["ACTUAL", "OLD"],
		md: 6,
		tab: "Details",
	},
};

const makeTableConfig = (key, label, listType, overrides = {}) => ({
	key,
	label,
	listType,
	apiSegment: key,
	editableFields: CUSTOMER_EDITABLE_FIELDS,
	lockedOnEditFields: LOCKED_ON_EDIT_FIELDS,
	addRequiredFields: ADD_REQUIRED_FIELDS,
	editRequiredFields: EDIT_REQUIRED_FIELDS,
	addSubtitle: "Egon Civico ID and status are required",
	defaultTabs: ["Details"],
	preferConfiguredSchema: false,
	...overrides,
});

export const COVERAGE_TABLES = {
	blacklist_customer: makeTableConfig(
		"blacklist_customer",
		"Blacklist Customer",
		COVERAGE_LIST_TYPES.BLACKLIST
	),
	blacklist_customer_fc_fttc: makeTableConfig(
		"blacklist_customer_fc_fttc",
		"Blacklist Customer FC FTTC",
		COVERAGE_LIST_TYPES.BLACKLIST
	),
	blacklist_customer_fc_ftth: makeTableConfig(
		"blacklist_customer_fc_ftth",
		"Blacklist Customer FC FTTH",
		COVERAGE_LIST_TYPES.BLACKLIST
	),
	blacklist_customer_fw_fttc: makeTableConfig(
		"blacklist_customer_fw_fttc",
		"Blacklist Customer FW FTTC",
		COVERAGE_LIST_TYPES.BLACKLIST
	),
	blacklist_pop: makeTableConfig(
		"blacklist_pop",
		"Blacklist POP",
		COVERAGE_LIST_TYPES.BLACKLIST,
		{
			editableFields: BLACKLIST_POP_FIELDS,
			addEditableFields: BLACKLIST_POP_FIELDS,
			editEditableFields: BLACKLIST_POP_FIELDS,
			lockedOnEditFields: new Set(),
			addRequiredFields: BLACKLIST_POP_ADD_REQUIRED_FIELDS,
			editRequiredFields: BLACKLIST_POP_EDIT_REQUIRED_FIELDS,
			addSubtitle: "Sky Site Code and status are required",
			preferConfiguredSchema: true,
		}
	),
	whitelist_customer: makeTableConfig(
		"whitelist_customer",
		"Whitelist Customer",
		COVERAGE_LIST_TYPES.WHITELIST
	),
};

export const COVERAGE_TABLE_KEYS = Object.keys(COVERAGE_TABLES);

export const getCoverageTableConfig = (tableKey) => {
	if (!tableKey) return null;
	const normalized = String(tableKey).trim().toLowerCase();
	return COVERAGE_TABLES[normalized] || null;
};

export const isAddMode = (mode) => String(mode || "").toLowerCase() === "add";

export const isDeleteMode = (mode) => String(mode || "").toLowerCase() === "delete";

export const shouldPreferConfiguredSchema = (tableConfig) =>
	!!tableConfig &&
	(tableConfig.preferConfiguredSchema === true ||
		tableConfig.key === "blacklist_pop");

const normalizeFieldName = (key) =>
	String(key || "")
		.toLowerCase()
		.replace(/[\s_]+/g, "");

const findCaseInsensitiveFieldKey = (record, key, { requireFilled = false } = {}) => {
	if (!record || key == null) return undefined;
	const lower = String(key).toLowerCase();
	return Object.keys(record).find((candidate) => {
		if (candidate.toLowerCase() !== lower) return false;
		return !requireFilled || isFilled(record[candidate]);
	});
};

const resolveSkySiteCodeValue = (record) => {
	if (!record) return undefined;

	for (const alias of BLACKLIST_POP_SKY_SITE_ALIASES) {
		if (isFilled(record[alias])) {
			return record[alias];
		}
	}

	const target = normalizeFieldName(BLACKLIST_POP_SKY_SITE_KEY);
	for (const candidate of Object.keys(record)) {
		if (
			normalizeFieldName(candidate) === target &&
			isFilled(record[candidate])
		) {
			return record[candidate];
		}
	}

	for (const alias of BLACKLIST_POP_SKY_SITE_ALIASES) {
		if (Object.prototype.hasOwnProperty.call(record, alias)) {
			return record[alias];
		}
	}

	return undefined;
};

export const normalizeCoverageRecord = (record, tableConfig) => {
	if (!record || !tableConfig) return record;

	if (!shouldPreferConfiguredSchema(tableConfig)) {
		return record;
	}

	const normalized = { ...record };
	const canonical = BLACKLIST_POP_SKY_SITE_KEY;
	const value = resolveSkySiteCodeValue(normalized);

	if (value !== undefined) {
		normalized[canonical] = value;
	}

	return normalized;
};

const getRecordFieldValue = (record, key) => {
	if (!record) return undefined;

	if (key === BLACKLIST_POP_SKY_SITE_KEY) {
		return resolveSkySiteCodeValue(record);
	}

	if (
		Object.prototype.hasOwnProperty.call(record, key) &&
		isFilled(record[key])
	) {
		return record[key];
	}

	const filledKey = findCaseInsensitiveFieldKey(record, key, {
		requireFilled: true,
	});
	if (filledKey != null) {
		return record[filledKey];
	}

	if (Object.prototype.hasOwnProperty.call(record, key)) {
		return record[key];
	}

	const anyKey = findCaseInsensitiveFieldKey(record, key);
	return anyKey != null ? record[anyKey] : undefined;
};

export const resolveEditableFields = (
	tableConfig,
	record,
	{ addMode = false } = {}
) => {
	// Add and Edit share one canonical POP field set (keys = API names).
	if (tableConfig?.key === "blacklist_pop") {
		return BLACKLIST_POP_FIELDS;
	}

	if (addMode && tableConfig?.addEditableFields?.length) {
		return tableConfig.addEditableFields;
	}
	if (!addMode && tableConfig?.editEditableFields?.length) {
		return tableConfig.editEditableFields;
	}

	const configured = tableConfig?.editableFields || [];
	if (configured.length) return configured;

	return Object.keys(record || {}).filter((key) => !COVERAGE_META_FIELDS.has(key));
};

const getLockedOnEditFields = (tableConfig) =>
	tableConfig?.lockedOnEditFields || LOCKED_ON_EDIT_FIELDS;

const getRequiredFields = (tableConfig, addMode) => {
	if (tableConfig?.key === "blacklist_pop") {
		return addMode
			? BLACKLIST_POP_ADD_REQUIRED_FIELDS
			: BLACKLIST_POP_EDIT_REQUIRED_FIELDS;
	}
	if (addMode) {
		return tableConfig?.addRequiredFields || ADD_REQUIRED_FIELDS;
	}
	return tableConfig?.editRequiredFields || EDIT_REQUIRED_FIELDS;
};

const applyFieldRules = (key, fieldDef, editMode, tableConfig) => {
	const locked = editMode && getLockedOnEditFields(tableConfig).has(key);

	if (locked) {
		return { ...fieldDef, disabled: true, readOnly: true, mandatory: false };
	}

	const mandatory = getRequiredFields(tableConfig, !editMode).includes(key);

	return { ...fieldDef, mandatory };
};

export const validateCoverageRecord = (merged, { addMode, tableConfig } = {}) => {
	const requiredFields = getRequiredFields(tableConfig, addMode);
	const errors = {};

	requiredFields.forEach((key) => {
		if (!isFilled(getRecordFieldValue(merged, key))) {
			const label = FIELD_DEFS[key]?.title || key;
			errors[key] = `${label} is required`;
		}
	});

	return {
		valid: Object.keys(errors).length === 0,
		errors,
	};
};

export const buildSchemaForFields = (
	fields,
	{ editMode = false, tableConfig = null } = {}
) =>
	Object.fromEntries(
		fields.map((key) => {
			const fieldDef = FIELD_DEFS[key] || {
				title: key,
				type: "text",
				md: 6,
				tab: "Details",
			};

			return [key, applyFieldRules(key, fieldDef, editMode, tableConfig)];
		})
	);

export const resolveSchemaFromRecord = (record, tableConfig, editMode = false) => {
	const addMode = !editMode;

	if (shouldPreferConfiguredSchema(tableConfig)) {
		return buildSchemaForFields(
			resolveEditableFields(tableConfig, record, { addMode }),
			{
				editMode,
				tableConfig,
			}
		);
	}

	if (record?.json_schema && Object.keys(record.json_schema).length > 0) {
		const schema = { ...record.json_schema };

		Object.keys(schema).forEach((key) => {
			schema[key] = applyFieldRules(key, schema[key], editMode, tableConfig);
		});

		return schema;
	}

	return buildSchemaForFields(
		resolveEditableFields(tableConfig, record, { addMode }),
		{
			editMode,
			tableConfig,
		}
	);
};

export const buildFormDataFromRecord = (
	record,
	tableConfig,
	{ addMode = false } = {}
) => {
	if (!tableConfig) return {};

	const formData = {};
	resolveEditableFields(tableConfig, record, { addMode }).forEach((key) => {
		const value = getRecordFieldValue(record, key);
		formData[key] =
			value != null && value !== "null" ? String(value) : "";
	});

	return formData;
};

export const buildUpsertRow = (tableKey, merged, { editMode = false } = {}) => {
	const tableConfig = getCoverageTableConfig(tableKey);
	if (!tableConfig) return null;

	const lockedFields = getLockedOnEditFields(tableConfig);
	const addMode = !editMode;
	const row = {};
	const fields = resolveEditableFields(tableConfig, merged, { addMode }).filter(
		(key) => !(editMode && lockedFields.has(key)) && key !== "id"
	);

	if (merged?.id) {
		row.id = merged.id;
	}

	fields.forEach((key) => {
		const value = getRecordFieldValue(merged, key);
		if (value != null && value !== "") {
			row[key] = String(value);
		}
	});

	return row;
};

export const createEmptyRecord = (tableKey) => {
	const tableConfig = getCoverageTableConfig(tableKey);
	if (!tableConfig) return null;

	const record = { id: null };
	resolveEditableFields(tableConfig, record, { addMode: true }).forEach(
		(key) => {
			record[key] = "";
		}
	);

	return {
		record,
		schema: resolveSchemaFromRecord(record, tableConfig, false),
		formData: buildFormDataFromRecord(record, tableConfig, { addMode: true }),
	};
};
